<template>
<div>
    <h3 class="mt-3">{{index + 1}} - {{question.translation.name}}</h3>
    <template v-if="question.answer || question.upload">
        <badge v-if="exerciseWaiting" class="mb-2" type="info">
            <i class="white mdi mdi-clock-outline"/>
            <span class="white">{{$t('lesson.exercise_waiting')}}</span>
        </badge>
        <badge v-else-if="!question.approve" class="mb-2" type="success">
            <i class="white mdi mdi-check-all"/>
            <span class="white">{{$t('lesson.exercise_revised')}}</span>
        </badge>
        <template v-else class="d-flex flex-row">
            <badge
                v-if="question.answer && question.answer.approve_question"
                class="mb-2"
                type="success"
            >
                <i class="white mdi mdi-check-all"/>
                <span class="white">{{$t('lesson.approved')}}</span>
            </badge>
            <badge v-else class="mb-2" type="danger">
                <i class="white mdi mdi-check-all"/>
                <span class="white">{{$t('lesson.reproved')}}</span>
            </badge>
        </template>
        <div v-if="question.answer && question.answer.justify_obs">
            <h4>{{$t('lesson.obs')}}</h4>
            <p class="text-muted">
                <i v-html="question.answer.justify_obs"/>
            </p>
            <div class="d-flex justify-content-end">
                <h4>
                    {{$t('lesson.revised_by')}}
                    <span class="text-muted">
                        {{question.answer.justified_by}}
                    </span>
                </h4>
            </div>
        </div>
        <div v-if="question.answer && question.answer.correction_date">
            <h4 class="d-flex justify-content-end">
                {{$t('lesson.corrected_on')}}:
                <span class="text-muted ml-1">
                    {{ formatDate(question.answer.correction_date) }}
                </span>
            </h4>
        </div>
    </template>
    <span
        v-if="question.translation.description"
        class="mb-3 d-block flex-c"
        v-html="question.translation.description"
    />
    <p
        v-if="question.translation.observation"
        style="font-style: italic"
        class="mb-3 d-block"
    >
        {{question.translation.observation}}
    </p>
    <editor
        v-if="!isFinished"
        v-model="answer"
        :name="$t('lesson.answer_placeholder')"
        class="nch--html-editor"
    />
    <p v-else class="answer" v-html="answer"/>
    <div class="d-flex">
        <a
            v-if="question.upload && isFinished && answer && Object.keys(question.upload).length"
            :style="$bgColor(color.primary)"
            :href="file(question.upload)"
            id="download_button"
            class="btn d-flex"
            target="_blank"
        >
            <i class="fas fa-file-alt" id="download_button"/>
            <p class="text">{{$t('lesson.answer_download')}}</p>
        </a>
        <template v-if="!isFinished">
            <base-button
                @click="modal=true"
                :class="{ verifyValidate: verifyValidate && error.has('upload') }"
                type="secondary"
                outline
            >
                <span class="mdi mdi-paperclip">{{$t('common.attach')}}</span>
            </base-button>
        </template>
        <upload-dropzone-modal
            v-model="upload"
            @success="filesChange"
            @close="modal = false"
            :modal="modal"
            ref="dropzone"
            v-validate="'required'"
            data-vv-name="upload"
        />
    </div>
    <span
        v-if="error.has('upload') && verifyValidate"
        class="invalid-feedback"
    >
        {{ error.first("upload") }}
    </span>
</div>
</template>

<script>
import { mapGetters } from 'vuex';

import BaseButton from '../../../../argon/components/BaseButton';
import Badge from '../../../../argon/components/Badge';
import FileInput from '../../../../argon/components/Inputs/FileInput';
import debounce from '../../../../helpers/helpers';
import UploadDropzoneModal from '../../../Common/Upload/UploadDropzoneModal';
import Editor from '../../../../argon/components/Inputs/HtmlEditor';

import formatDate from '../../../../mixins/FormatDate';

export default {
    name: 'ClassExerciseUpload',

    mixins: [formatDate],

    components: {
        BaseButton,
        Badge,
        FileInput,
        UploadDropzoneModal,
        Editor,
    },

    data() {
        return {
            modal: false,
            upload: [],
            answer: '',
            uploadLoading: false,
            deleteLoading: false,
            uploadSubmited: false,
            fileChanged: false,
            error: this.$validator.errors,
        };
    },
    props: {
        index: {
            type: Number,
            required: true,
        },
        question: {
            type: Object,
            required: true,
        },
        isFinished: Boolean,
        verifyValidate: Boolean,
    },
    watch: {
        answer: debounce(function unnamed() {
            if (!this.isFinished) {
                this.submit();
            }
        }, 500),

        question(newVal) {
            if (newVal.answer) {
                this.answer = newVal.answer.text;
                this.upload = [];
                this.uploadSubmited = false;
            } else {
                this.upload = [];
                this.uploadSubmited = false;
            }
        },
    },
    computed: {
        ...mapGetters({
            color: 'COLOR',
            exercise: 'CLASS_EXERCISE',
            user: 'USER',
        }),
        exerciseWaiting() {
            const sameAnswer = this.question.answer === this.answer;
            return sameAnswer || (this.question.answer && this.question.answer.grade === null);
        },
    },
    methods: {
        filesChange(upload) {
            if (!upload.host || !upload.hash || !upload.name ||
                !upload.type || !upload.size || !upload.mime) {
                this.$notify({
                    type: 'danger',
                    message: this.$t('content.exercise.error.upload'),
                });
                return false;
            }
            this.upload = {
                host: upload.host,
                filename: upload.name,
                hash: upload.hash,
                type: upload.type,
                size: upload.size,
                mime: upload.mime,
                original_file: upload.original_file,
                status: 1,
                author_id: this.user.id,
                width: upload.width,
                height: upload.height,
                path: upload.path,
                // Não é coluna real (some no submit ao backend), mas sem isso o
                // preview local perde as urls assinadas do S3 e cai no fallback
                // /storage/ antigo, quebrando a visualização/redirect do arquivo.
                urls: upload.urls,
                // gallery_id:
            };
            this.fileChanged = true;
            this.submit();
            return true;
        },

        async submit() {
            this.uploadLoading = true;

            if (this.fileChanged) {
                // await this.uploadFile(this.upload);
                this.uploadSubmited = true;
                // urls não é coluna real (é só pro preview local) — mandar pro
                // backend quebra o update() do model Upload ("Column not found: urls").
                const { urls, ...uploadPayload } = this.upload;
                this.$store.dispatch('CLASS_EXERCISE_SUBMIT_OPTION', {
                    questionId: this.question.id,
                    answer: {
                        text: this.answer,
                        upload: uploadPayload,
                    },
                });
            } else {
                const response = await this.$store.dispatch('CLASS_EXERCISE_SUBMIT_OPTION', {
                    questionId: this.question.id,
                    answer: {
                        text: this.answer,
                    },
                });

                if (!response) {
                    this.answer = '';
                    this.upload = [];
                    this.$notify({
                        type: 'danger',
                        message: this.$t('content.exercise.error.no_connection'),
                    });
                }
            }
            this.uploadLoading = false;
            this.fileChanged = false;
        },

        file(upload) {
            // S3: usa URL assinada gerada pelo model Upload (urls.download/original).
            if (upload && upload.urls && (upload.urls.download || upload.urls.original)) {
                return upload.urls.download || upload.urls.original;
            }
            // Fallback disco local.
            return `/storage/${upload.host || null}/original/${upload.original_file}`;
        },
    },
    mounted() {
        this.answer = this.question.answer ? this.question.answer.text : this.answer;
    },
};
</script>

<style lang="scss" scoped>
.white {
    color: white
}
.invalid-feedback {
    position: absolute;
    display: block;
}

#download_button {
    color: white;

    i {
        font-size: 1.4rem;
    }

    .text {
        margin-left: 0.6rem;
        font-family: inherit;
        font-size: 0.9rem;
        font-weight: bold;
        margin: 0;
    }
}
.flex-c{
    display: flex !important;
    flex-direction: column;
}
</style>

<style>
    .answer p > img,
    .justify p > img {
        width: 100%;
    }
    .answer {
        padding: 10px;
        max-height: 200px;
        overflow: auto;
        background-color: #f4f5f7;
    }
    .verifyValidate{
        border: 2px solid red;
    }
</style>
